// apps/merchant-v3/src/lib/scope/usePermissions.ts
//
// Phase 6.1 Wave 5 Task 24 — FE permission reader for role-gating the
// Members / Teams settings surfaces (P6.1.D-27, G-15). Consumes the BE
// GET /api/v1/auth/me/permissions flat array (permission.service resolve —
// owner/admin hold `member:*`/`team:*`, manager holds `member:read`/
// `team:read`/`scope:read`, staff/viewer hold only `scope:read`).
//
// The gate is UX-layer only — the API already enforces the same permissions
// server-side (Wave 3 + Task 18). This mirrors that truth so staff/viewer
// never see a half-rendered mutate surface and manager gets a read-only view.
//
// Wildcard semantics mirror the BE permission middleware: a granted `<mod>:*`
// (or `*:*`) key covers every `<mod>:<action>` check.
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-27
// vg-binding: P6.1.D-02
// vg-binding: G-15
// vg-binding: G-36
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient, ApiError } from '@/lib/api/client';

interface MePermissionsWire {
  permissions?: unknown;
  permsVersion?: unknown;
}

/** Stable React Query cache key for the caller permission set. */
export const ME_PERMISSIONS_QUERY_KEY = ['auth', 'me', 'permissions'] as const;

const PERMS_STALE_MS = 60_000;

async function fetchMePermissions(): Promise<string[]> {
  try {
    const raw = await apiClient.get<MePermissionsWire>(
      '/api/v1/auth/me/permissions',
    );
    return Array.isArray(raw?.permissions)
      ? raw.permissions.filter((x): x is string => typeof x === 'string')
      : [];
  } catch (err) {
    // Fail-closed: on 403/404/network the caller resolves to zero perms
    // (no mutate surface rendered). 401 re-throws for RequireAuth.
    if (err instanceof ApiError && err.status === 401) throw err;
    return [];
  }
}

/** True when `granted` satisfies the exact key OR a covering wildcard. */
function grantMatches(granted: ReadonlySet<string>, key: string): boolean {
  if (granted.has('*:*')) return true;
  if (granted.has(key)) return true;
  const module = key.split(':')[0];
  return module ? granted.has(`${module}:*`) : false;
}

/**
 * The member's derived capability tier for the settings surfaces (D-02/D-27).
 *   - 'full'      → owner/admin (can mutate members + teams)
 *   - 'readonly'  → manager (can read but not mutate)
 *   - 'none'      → staff/viewer (no access → 403 page)
 *   - 'loading'   → permissions not yet resolved
 */
export type SettingsAccessTier = 'full' | 'readonly' | 'none' | 'loading';

export interface UsePermissionsResult {
  permissions: string[];
  isLoading: boolean;
  isError: boolean;
  /** Exact/wildcard permission check (e.g. `has('member:invite')`). */
  has: (key: string) => boolean;
  /** owner/admin — may create/edit/toggle members + teams. */
  canManageMembers: boolean;
  canManageTeams: boolean;
  /** manager — may read members/teams but not mutate. */
  canReadMembers: boolean;
  canReadTeams: boolean;
  /** Settings-page access tier (drives 403 vs read-only vs full). */
  settingsAccess: SettingsAccessTier;
}

/**
 * Read + derive the caller's settings-surface capabilities from the resolved
 * permission array. Cached 60s; shared across the route guards + team page.
 */
export function usePermissions(): UsePermissionsResult {
  const query: UseQueryResult<string[], ApiError> = useQuery<string[], ApiError>(
    {
      queryKey: ME_PERMISSIONS_QUERY_KEY,
      queryFn: fetchMePermissions,
      staleTime: PERMS_STALE_MS,
      gcTime: 5 * 60_000,
      retry: (failureCount, err) => {
        if (err instanceof ApiError && err.status === 401) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  );

  return useMemo<UsePermissionsResult>(() => {
    const list = query.data ?? [];
    const granted = new Set(list);
    const has = (key: string): boolean => grantMatches(granted, key);

    const canManageMembers =
      has('member:invite') || has('member:toggle') || has('member:edit');
    const canManageTeams =
      has('team:invite') || has('team:reassign') || has('team:create');
    const canReadMembers = has('member:read') || canManageMembers;
    const canReadTeams = has('team:read') || canManageTeams;

    let settingsAccess: SettingsAccessTier;
    if (query.isLoading) {
      settingsAccess = 'loading';
    } else if (canManageMembers || canManageTeams) {
      settingsAccess = 'full';
    } else if (canReadMembers || canReadTeams) {
      settingsAccess = 'readonly';
    } else {
      settingsAccess = 'none';
    }

    return {
      permissions: list,
      isLoading: query.isLoading,
      isError: query.isError,
      has,
      canManageMembers,
      canManageTeams,
      canReadMembers,
      canReadTeams,
      settingsAccess,
    };
  }, [query.data, query.isLoading, query.isError]);
}
